import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Divider, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import PrimaryActionButton from '../common/PrimaryActionButton';
import { fieldLabelRowSx } from '../common/formFieldStyles';
import StoreService from '../../services/storeService';
import { useToast } from '../../context/ToastContext';
import { BRAND } from '../../theme/brandTokens';

export const STATEMENT_DESCRIPTOR_MAX_LENGTH = 22;

// Mirrors the API's formatter so the preview shows what the bank statement will really say.
export const formatStatementDescriptorPreview = (value) => (
  String(value || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9.,&-]+/g, ' ')
    .trim()
    .slice(0, STATEMENT_DESCRIPTOR_MAX_LENGTH)
    .trim()
);

const inputSx = (hasError) => ({
  width: '100%',
  padding: '12px 16px',
  border: `2px solid ${hasError ? '#f44336' : BRAND.line}`,
  borderRadius: '8px',
  fontSize: '16px',
  fontFamily: 'inherit',
  outline: 'none',
  backgroundColor: '#fff',
  boxSizing: 'border-box',
  minHeight: 48,
  '&:focus': { borderColor: BRAND.accent },
});

/**
 * A charge the customer does not recognise on their statement is one of the easiest disputes to
 * open, so the owner can make sure it shows their brand rather than their company's legal name.
 * Saved explicitly, unlike the toggles above, because the payment provider can reject a value.
 */
const StoreStatementDescriptorSection = ({ storeId }) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [value, setValue] = useState('');
  const [saved, setSaved] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    StoreService.getStatementDescriptor(storeId)
      .then((result) => {
        if (cancelled) return;
        setSaved(result);
        setValue(result.statementDescriptor || '');
      })
      .catch((error) => {
        console.error('Failed to load statement descriptor:', error);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [storeId]);

  const preview = formatStatementDescriptorPreview(value || saved?.suggestedStatementDescriptor);
  const isValid = preview.length >= 5 && /[A-Z]/.test(preview);
  const isDirty = saved !== null && value !== (saved.statementDescriptor || '');

  const handleSave = async () => {
    try {
      setSaving(true);
      const result = await StoreService.updateStatementDescriptor(storeId, value.trim());
      setSaved(result);
      setValue(result.statementDescriptor || '');
      showSuccess(t('paymentSettingsPage.customerPayments.statementDescriptor.success'));
    } catch (error) {
      console.error('Failed to save statement descriptor:', error);
      showError(t('paymentSettingsPage.customerPayments.statementDescriptor.error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Divider />
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 600, color: BRAND.primaryText, mb: 1 }}>
          {t('paymentSettingsPage.customerPayments.statementDescriptor.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {t('paymentSettingsPage.customerPayments.statementDescriptor.help')}
        </Typography>

        {loading ? (
          <CircularProgress size={20} sx={{ color: BRAND.accent }} />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxWidth: 420 }}>
            <Box>
              <Box sx={fieldLabelRowSx}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                  {t('paymentSettingsPage.customerPayments.statementDescriptor.label')}
                </Typography>
              </Box>
              <Box
                component="input"
                type="text"
                aria-label={t('paymentSettingsPage.customerPayments.statementDescriptor.label')}
                value={value}
                maxLength={40}
                placeholder={saved?.suggestedStatementDescriptor || ''}
                onChange={(event) => setValue(event.target.value)}
                sx={inputSx(Boolean(value) && !isValid)}
              />
            </Box>

            <Box sx={{ borderRadius: 1.5, p: 1.25, backgroundColor: BRAND.background, border: `1px dashed ${BRAND.line}` }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {t('paymentSettingsPage.customerPayments.statementDescriptor.preview')}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700, letterSpacing: 0.5 }} data-testid="statement-descriptor-preview">
                {preview || '—'}
              </Typography>
            </Box>

            {value && !isValid ? (
              <Typography variant="caption" sx={{ color: '#f44336' }}>
                {t('paymentSettingsPage.customerPayments.statementDescriptor.invalid')}
              </Typography>
            ) : null}

            <Box>
              <PrimaryActionButton onClick={handleSave} disabled={saving || !isDirty || (Boolean(value) && !isValid)}>
                {saving ? t('common.saving') : t('paymentSettingsPage.customerPayments.statementDescriptor.save')}
              </PrimaryActionButton>
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
};

export default StoreStatementDescriptorSection;
